/** @format */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  addDays,
  addWeeks,
  format,
  isToday,
  startOfDay,
  startOfWeek,
  subWeeks,
} from "date-fns";
import { ChevronLeft, ChevronRight, Plus, Trash } from "lucide-react";
import { ConfirmDialog } from "../ui/ConfirmDialog";

// ─── constants ────────────────────────────────────────────────────────────────

const HOUR_H = 32; // px per hour
const SNAP = 15; // minutes
const TOTAL_H = 24 * HOUR_H; // 1152 px
const MIN_DUR = 30; // minimum window duration in minutes

// ─── types ────────────────────────────────────────────────────────────────────

export interface WindowRow {
  start_at: string;
  end_at: string;
}

interface Props {
  windows: WindowRow[];
  onChange: (windows: WindowRow[]) => void;
}

type DragOp =
  | {
      op: "move";
      winIdx: number;
      durMs: number;
      minOffset: number;
      anchorDayIdx: number;
      anchorMin: number;
      curDayIdx: number;
      curMin: number;
    }
  | {
      op: "resize-end";
      winIdx: number;
      dayIdx: number;
      startDate: Date;
      curMin: number;
    }
  | {
      op: "resize-start";
      winIdx: number;
      dayIdx: number;
      endDate: Date;
      curMin: number;
    };

type Ghost = { start: Date; end: Date; dayIdx: number };

// ─── helpers ──────────────────────────────────────────────────────────────────

function snap(m: number) {
  return Math.round(m / SNAP) * SNAP;
}
function clampMin(m: number) {
  return Math.max(0, Math.min(24 * 60, m));
}
function pxToMin(y: number) {
  return clampMin(snap((y / TOTAL_H) * 24 * 60));
}
function minToPx(m: number) {
  return (m / (24 * 60)) * TOTAL_H;
}
function dateToMin(d: Date) {
  return d.getHours() * 60 + d.getMinutes();
}
function minToDate(m: number, day: Date): Date {
  const d = new Date(day);
  d.setHours(Math.floor(m / 60), m % 60, 0, 0);
  return d;
}
function toIso(d: Date): string {
  return d.toISOString();
}
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

// ─── component ────────────────────────────────────────────────────────────────

export default function WeeklyWindowPicker({ windows, onChange }: Props) {
  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 0 }),
  );
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [now, setNow] = useState(new Date());
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState<number | null>(null);
  const [editPopover, setEditPopover] = useState<{
    winIdx: number;
    x: number;
    y: number;
  } | null>(null);
  const [popoverStart, setPopoverStart] = useState("");
  const [popoverEnd, setPopoverEnd] = useState("");
  // bump this to force a re-render from ref mutations during drag
  const [tick, setTick] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragOp | null>(null);
  const weekDaysRef = useRef<Date[]>([]);
  const popoverRef = useRef<HTMLDivElement>(null);

  // ── derived ──────────────────────────────────────────────────────────────────

  const weekDays = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    weekDaysRef.current = days;
    return days;
  }, [weekStart]);

  const parsed = useMemo(
    () =>
      windows
        .map((w, idx) => {
          const s = w.start_at ? new Date(w.start_at) : null;
          const e = w.end_at ? new Date(w.end_at) : null;
          if (!s || !e || isNaN(s.getTime()) || isNaN(e.getTime())) return null;
          return { idx, start: s, end: e };
        })
        .filter(Boolean) as { idx: number; start: Date; end: Date }[],
    [windows],
  );

  // ── ghost & drag-state derived from tick + dragRef ────────────────────────

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ghost = useMemo((): Ghost | null => {
    const d = dragRef.current;
    if (!d) return null;
    const days = weekDaysRef.current;
    if (!days.length) return null;

    if (d.op === "move") {
      const day = days[d.curDayIdx] ?? days[0];
      const s = minToDate(clampMin(d.curMin - d.minOffset), day);
      return { start: s, end: new Date(s.getTime() + d.durMs), dayIdx: d.curDayIdx };
    }
    if (d.op === "resize-end") {
      const candidate = minToDate(d.curMin, days[d.dayIdx]);
      const minEnd = new Date(d.startDate.getTime() + MIN_DUR * 60000);
      return {
        start: d.startDate,
        end: candidate >= minEnd ? candidate : minEnd,
        dayIdx: d.dayIdx,
      };
    }
    if (d.op === "resize-start") {
      const candidate = minToDate(d.curMin, days[d.dayIdx]);
      const maxStart = new Date(d.endDate.getTime() - MIN_DUR * 60000);
      return {
        start: candidate <= maxStart ? candidate : maxStart,
        end: d.endDate,
        dayIdx: d.dayIdx,
      };
    }
    return null;
  }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { movingIdx, resizingIdx } = useMemo(() => {
    const d = dragRef.current;
    if (!d) return { movingIdx: null, resizingIdx: null };
    if (d.op === "move") return { movingIdx: d.winIdx, resizingIdx: null };
    if (d.op === "resize-end" || d.op === "resize-start")
      return { movingIdx: null, resizingIdx: d.winIdx };
    return { movingIdx: null, resizingIdx: null };
  }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = HOUR_H * 8;
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!editPopover) return;
    const w = windows[editPopover.winIdx];
    if (w) {
      setPopoverStart(isoToLocalInput(w.start_at));
      setPopoverEnd(isoToLocalInput(w.end_at));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPopover?.winIdx]);

  useEffect(() => {
    if (!editPopover) return;
    const onDown = (e: PointerEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setEditPopover(null);
      }
    };
    document.addEventListener("pointerdown", onDown, { capture: true });
    return () => document.removeEventListener("pointerdown", onDown, { capture: true });
  }, [editPopover]);

  // ── coordinate helpers ────────────────────────────────────────────────────

  const clientToMin = useCallback(
    (clientY: number) => {
      const scrollEl = scrollRef.current;
      if (!scrollEl) return 0;
      return pxToMin(clientY - scrollEl.getBoundingClientRect().top + scrollEl.scrollTop);
    },
    [],
  );

  const clientToDayIdx = useCallback((clientX: number): number => {
    const cols =
      containerRef.current?.querySelectorAll<HTMLElement>("[data-day-col]");
    if (!cols) return -1;
    for (let i = 0; i < cols.length; i++) {
      const r = cols[i].getBoundingClientRect();
      if (clientX >= r.left && clientX < r.right) return i;
    }
    return -1;
  }, []);

  // ── delete ────────────────────────────────────────────────────────────────

  const deleteWindow = useCallback(
    (idx: number) => {
      const w = parsed.find((p) => p.idx === idx);
      if (w && w.start <= now && w.end >= now) {
        setConfirmDeleteIdx(idx);
      } else {
        onChange(windows.filter((_, i) => i !== idx));
        setSelectedIdx(null);
      }
    },
    [parsed, now, onChange, windows],
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT"
      )
        return;
      if (
        (e.key === "Delete" || e.key === "Backspace") &&
        selectedIdx !== null &&
        confirmDeleteIdx === null
      ) {
        e.preventDefault();
        deleteWindow(selectedIdx);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [selectedIdx, confirmDeleteIdx, deleteWindow]);

  // ── double-click create ───────────────────────────────────────────────────

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent, dayIdx: number) => {
      const scrollEl = scrollRef.current;
      if (!scrollEl) return;
      const scrollRect = scrollEl.getBoundingClientRect();
      const y = e.clientY - scrollRect.top + scrollEl.scrollTop;
      const m = pxToMin(y);
      const endM = clampMin(m + 60);
      const day = weekDaysRef.current[dayIdx];
      if (!day) return;
      onChange([
        ...windows,
        { start_at: toIso(minToDate(m, day)), end_at: toIso(minToDate(endM, day)) },
      ]);
      setSelectedIdx(windows.length);
    },
    [onChange, windows],
  );

  // ── drag start ────────────────────────────────────────────────────────────

  const startMove = useCallback(
    (e: React.PointerEvent, winIdx: number, dayIdx: number) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      containerRef.current?.setPointerCapture(e.pointerId);
      const w = parsed.find((p) => p.idx === winIdx);
      if (!w) return;
      const durMs = w.end.getTime() - w.start.getTime();
      const clickMin = clientToMin(e.clientY);
      const minOffset = clickMin - dateToMin(w.start);
      dragRef.current = {
        op: "move",
        winIdx,
        durMs,
        minOffset,
        anchorDayIdx: dayIdx,
        anchorMin: clickMin,
        curDayIdx: dayIdx,
        curMin: clickMin,
      };
      setSelectedIdx(winIdx);
      setTick((t) => t + 1);
    },
    [parsed, clientToMin],
  );

  const startResizeEnd = useCallback(
    (e: React.PointerEvent, winIdx: number, dayIdx: number) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      containerRef.current?.setPointerCapture(e.pointerId);
      const w = parsed.find((p) => p.idx === winIdx);
      if (!w) return;
      const colDay = weekDaysRef.current[dayIdx];
      const colDayEnd = new Date(startOfDay(colDay).getTime() + 24 * 60 * 60 * 1000);
      // If the window ends on a later day, the handle sits at the bottom of this column (1440)
      const initCurMin =
        w.end.getTime() >= colDayEnd.getTime() ? 1440 : dateToMin(w.end);
      dragRef.current = {
        op: "resize-end",
        winIdx,
        dayIdx,
        startDate: w.start,
        curMin: initCurMin,
      };
      setTick((t) => t + 1);
    },
    [parsed],
  );

  const startResizeStart = useCallback(
    (e: React.PointerEvent, winIdx: number, dayIdx: number) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      containerRef.current?.setPointerCapture(e.pointerId);
      const w = parsed.find((p) => p.idx === winIdx);
      if (!w) return;
      const colDay = weekDaysRef.current[dayIdx];
      // If the window starts on an earlier day, the handle sits at the top of this column (0)
      const initCurMin =
        w.start.getTime() <= startOfDay(colDay).getTime() ? 0 : dateToMin(w.start);
      dragRef.current = {
        op: "resize-start",
        winIdx,
        dayIdx,
        endDate: w.end,
        curMin: initCurMin,
      };
      setTick((t) => t + 1);
    },
    [parsed],
  );

  // ── drag move / commit ────────────────────────────────────────────────────

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;

      if (d.op === "move") {
        const di = clientToDayIdx(e.clientX);
        const curDayIdx = di >= 0 ? di : d.curDayIdx;
        dragRef.current = {
          ...d,
          curDayIdx,
          curMin: clientToMin(e.clientY),
        };
      } else {
        dragRef.current = { ...d, curMin: clientToMin(e.clientY) };
      }
      setTick((t) => t + 1);
    },
    [clientToMin, clientToDayIdx],
  );

  const commit = useCallback(
    (e: React.PointerEvent) => {
      const d = dragRef.current;
      dragRef.current = null;
      setTick((t) => t + 1);
      if (!d) return;

      const days = weekDaysRef.current;

      if (d.op === "move") {
        const day = days[d.curDayIdx] ?? days[0];
        const s = minToDate(clampMin(d.curMin - d.minOffset), day);
        const end = new Date(s.getTime() + d.durMs);

        // Tap detection: compare raw pointer position, not the derived window
        // position — avoids false positives on multi-day windows where clicking
        // a non-start column produces a large date delta even with no movement.
        const moved =
          d.curDayIdx !== d.anchorDayIdx ||
          Math.abs(d.curMin - d.anchorMin) >= SNAP;

        if (!moved) {
          setSelectedIdx(d.winIdx);
          setEditPopover({ winIdx: d.winIdx, x: e.clientX, y: e.clientY });
          return;
        }

        onChange(
          windows.map((w, i) =>
            i === d.winIdx
              ? { start_at: toIso(s), end_at: toIso(end) }
              : w,
          ),
        );
      } else if (d.op === "resize-end") {
        const candidate = minToDate(d.curMin, days[d.dayIdx]);
        const minEnd = new Date(d.startDate.getTime() + MIN_DUR * 60000);
        const newEnd = candidate >= minEnd ? candidate : minEnd;
        onChange(
          windows.map((w, i) =>
            i === d.winIdx ? { ...w, end_at: toIso(newEnd) } : w,
          ),
        );
      } else if (d.op === "resize-start") {
        const candidate = minToDate(d.curMin, days[d.dayIdx]);
        const maxStart = new Date(d.endDate.getTime() - MIN_DUR * 60000);
        const newStart = candidate <= maxStart ? candidate : maxStart;
        onChange(
          windows.map((w, i) =>
            i === d.winIdx ? { ...w, start_at: toIso(newStart) } : w,
          ),
        );
      }
    },
    [windows, onChange],
  );

  // ── block renderer ────────────────────────────────────────────────────────

  const renderBlock = (
    key: string,
    start: Date,
    end: Date,
    day: Date,
    dayIdx: number,
    winIdx: number | null,
    isGhost: boolean,
  ) => {
    const ds = startOfDay(day);
    const de = new Date(ds.getTime() + 24 * 60 * 60 * 1000);
    const clStart = start < ds ? ds : start;
    const clEnd = end > de ? de : end;
    if (clEnd.getTime() <= clStart.getTime()) return null;

    const top = minToPx(dateToMin(clStart));
    // When clEnd reaches midnight-of-next-day (de), dateToMin returns 0 — use 24*60 instead
    const clEndMin = clEnd.getTime() >= de.getTime() ? 24 * 60 : dateToMin(clEnd);
    const height = Math.max(minToPx(MIN_DUR), minToPx(clEndMin) - top);
    const isSel = !isGhost && selectedIdx === winIdx;
    const isFading = !isGhost && winIdx !== null && movingIdx === winIdx;

    return (
      <div
        key={key}
        style={{ position: "absolute", top, height, left: 2, right: 2 }}
        className={`rounded overflow-hidden select-none ${
          isGhost
            ? "bg-blue-400/25 border border-blue-400/60 pointer-events-none"
            : isFading
              ? "bg-blue-600/30 border border-blue-400/30 opacity-40 pointer-events-none"
              : isSel
                ? "bg-blue-500/80 border border-blue-300 shadow-md cursor-grab active:cursor-grabbing"
                : "bg-blue-600/55 border border-blue-400/60 cursor-grab active:cursor-grabbing hover:bg-blue-500/65"
        }`}
        onPointerDown={
          isGhost
            ? undefined
            : (e) => {
                if (winIdx !== null) startMove(e, winIdx, dayIdx);
              }
        }
        onClick={(e) => {
          e.stopPropagation();
          if (!isGhost && winIdx !== null) setSelectedIdx(winIdx);
        }}
      >
        {!isGhost && winIdx !== null && (
          <div
            className="absolute top-0 left-0 right-0 h-2.5 cursor-n-resize z-10"
            onPointerDown={(e) => startResizeStart(e, winIdx, dayIdx)}
          />
        )}
        <div className="pointer-events-none px-1.5 pt-0.5 flex items-baseline gap-0.5 truncate">
          <span className="text-[10px] font-semibold text-blue-50 shrink-0">
            {format(clStart, "h:mm a")}
          </span>
          <span className="text-[9px] text-blue-300/60 shrink-0">–</span>
          <span className="text-[10px] text-blue-200/75 truncate">
            {format(clEnd, "h:mm a")}
          </span>
        </div>
        {!isGhost && winIdx !== null && (
          <div
            className="absolute bottom-0 left-0 right-0 h-2.5 cursor-s-resize z-10 flex items-center justify-center"
            onPointerDown={(e) => startResizeEnd(e, winIdx, dayIdx)}
          >
            <div className="h-0.5 w-5 rounded-full bg-blue-300/50" />
          </div>
        )}
      </div>
    );
  };

  // ── current time position ─────────────────────────────────────────────────

  const nowTop = minToPx(dateToMin(now));

  const addWindowAtDefault = useCallback(() => {
    const today = new Date();
    const day =
      weekDays.find(
        (d) => d.toDateString() === today.toDateString(),
      ) ?? weekDays[0];
    const startMin = snap(clampMin(dateToMin(today) + 1));
    const endMin = clampMin(startMin + 60);
    onChange([
      ...windows,
      { start_at: toIso(minToDate(startMin, day)), end_at: toIso(minToDate(endMin, day)) },
    ]);
    setSelectedIdx(windows.length);
  }, [weekDays, windows, onChange]);

  const timezone = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
        .formatToParts(new Date())
        .find((p) => p.type === "timeZoneName")?.value ?? "",
    [],
  );

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-2">
      {/* Nav header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setWeekStart((w) => subWeeks(w, 1))}
            className="rounded border border-slate-600 bg-slate-900 p-1 text-slate-300 hover:bg-slate-800"
            aria-label="Previous week"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() =>
              setWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }))
            }
            className="rounded border border-blue-500/40 bg-blue-500/15 px-2 py-0.5 text-xs font-medium text-blue-100 hover:bg-blue-500/25"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setWeekStart((w) => addWeeks(w, 1))}
            className="rounded border border-slate-600 bg-slate-900 p-1 text-slate-300 hover:bg-slate-800"
            aria-label="Next week"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <span className="ml-2 text-sm font-medium text-slate-300">
            {format(weekStart, "MMMM yyyy")}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {selectedIdx !== null && (
            <button
              type="button"
              onClick={() => deleteWindow(selectedIdx)}
              className="inline-flex items-center gap-1 rounded border border-rose-600/50 bg-rose-500/15 px-2 py-1 text-xs text-rose-300 hover:bg-rose-500/25"
            >
              <Trash className="h-3.5 w-3.5" />
              Delete
            </button>
          )}
          <button
            type="button"
            onClick={addWindowAtDefault}
            className="inline-flex items-center gap-1 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
          >
            <Plus className="h-3.5 w-3.5" />
            Add window
          </button>
        </div>
      </div>

      {/* Calendar */}
      <div className="overflow-hidden rounded-lg border border-slate-700/70">
        {/* Day header row */}
        <div
          className="grid border-b border-slate-700/70 bg-slate-900/80"
          style={{ gridTemplateColumns: "44px repeat(7, 1fr)" }}
        >
          <div className="flex items-end justify-center pb-1.5">
            <span className="text-[9px] font-medium text-slate-500">{timezone}</span>
          </div>
          {weekDays.map((day) => (
            <div
              key={day.toISOString()}
              className={`border-l border-slate-700/70 py-1.5 text-center ${
                isToday(day) ? "text-blue-300" : "text-slate-400"
              }`}
            >
              <div className="text-[8px] uppercase tracking-widest">
                {format(day, "EEE")}
              </div>
              <div
                className={`mx-auto mt-0.5 flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold ${
                  isToday(day) ? "bg-blue-500 text-white" : "text-slate-200"
                }`}
              >
                {format(day, "d")}
              </div>
            </div>
          ))}
        </div>

        {/* Scrollable time grid */}
        <div
          ref={scrollRef}
          className="overflow-y-auto bg-slate-950/30 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-thumb:hover]:bg-slate-600"
          style={{ maxHeight: 380 }}
        >
          <div
            ref={containerRef}
            className="relative grid select-none"
            style={{
              gridTemplateColumns: "44px repeat(7, 1fr)",
              height: TOTAL_H,
            }}
            onPointerMove={onPointerMove}
            onPointerUp={commit}
            onPointerCancel={commit}
          >
            {/* Hour labels column */}
            <div className="pointer-events-none relative">
              {Array.from({ length: 24 }, (_, h) => (
                <div
                  key={h}
                  className="absolute right-2 -translate-y-1/2 text-[10px] text-slate-500"
                  style={{ top: h * HOUR_H }}
                >
                  {h === 0 ? "" : format(new Date(2020, 0, 1, h), "h a")}
                </div>
              ))}
            </div>

            {/* Day columns */}
            {weekDays.map((day, dayIdx) => {
              const dayMs = startOfDay(day).getTime();
              const dayEndMs = dayMs + 24 * 60 * 60 * 1000;
              const dayWindows = parsed.filter(
                (w) =>
                  w.start.getTime() < dayEndMs && w.end.getTime() > dayMs,
              );

              return (
                <div
                  key={day.toISOString()}
                  data-day-col
                  className="relative border-l border-slate-700/70 cursor-default"
                  onClick={() => setSelectedIdx(null)}
                  onDoubleClick={(e) => handleDoubleClick(e, dayIdx)}
                >
                  {/* Hour lines */}
                  {Array.from({ length: 24 }, (_, h) => (
                    <div
                      key={h}
                      className={`pointer-events-none absolute left-0 right-0 border-t ${
                        h === 0
                          ? "border-slate-700/70"
                          : "border-slate-800/60"
                      }`}
                      style={{ top: h * HOUR_H }}
                    />
                  ))}
                  {/* Half-hour dashed lines */}
                  {Array.from({ length: 24 }, (_, h) => (
                    <div
                      key={`hh${h}`}
                      className="pointer-events-none absolute left-0 right-0 border-t border-dashed border-slate-800/25"
                      style={{ top: h * HOUR_H + HOUR_H / 2 }}
                    />
                  ))}

                  {/* Current time indicator */}
                  {isToday(day) && (
                    <div
                      className="pointer-events-none absolute left-0 right-0 z-20"
                      style={{ top: nowTop }}
                    >
                      <div className="flex items-center">
                        <div className="h-2 w-2 shrink-0 rounded-full bg-rose-500 -translate-x-1" />
                        <div className="h-px w-full bg-rose-500" />
                      </div>
                    </div>
                  )}

                  {/* Existing window blocks */}
                  {dayWindows.map((w) => {
                    if (resizingIdx === w.idx) return null; // ghost replaces it
                    return renderBlock(
                      `w${w.idx}-d${dayIdx}`,
                      w.start,
                      w.end,
                      day,
                      dayIdx,
                      w.idx,
                      false,
                    );
                  })}

                  {/* Ghost block (appears in every day column it spans) */}
                  {ghost &&
                    ghost.start.getTime() < dayEndMs &&
                    ghost.end.getTime() > dayMs &&
                    renderBlock(
                      `ghost-${dayIdx}`,
                      ghost.start,
                      ghost.end,
                      day,
                      dayIdx,
                      null,
                      true,
                    )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {editPopover !== null &&
        windows[editPopover.winIdx] &&
        createPortal(
          (() => {
            const POPOVER_W = 280;
            const POPOVER_H = 160;
            const left = Math.min(
              editPopover.x + 12,
              window.innerWidth - POPOVER_W - 16,
            );
            const top =
              editPopover.y + 12 + POPOVER_H > window.innerHeight
                ? editPopover.y - POPOVER_H - 12
                : editPopover.y + 12;

            const applyStart = (val: string) => {
              setPopoverStart(val);
              const d = new Date(val);
              if (isNaN(d.getTime())) return;
              onChange(
                windows.map((w, i) =>
                  i === editPopover.winIdx
                    ? { ...w, start_at: toIso(d) }
                    : w,
                ),
              );
            };
            const applyEnd = (val: string) => {
              setPopoverEnd(val);
              const d = new Date(val);
              if (isNaN(d.getTime())) return;
              onChange(
                windows.map((w, i) =>
                  i === editPopover.winIdx ? { ...w, end_at: toIso(d) } : w,
                ),
              );
            };

            return (
              <div
                ref={popoverRef}
                style={{ position: "fixed", left, top, zIndex: 9999, width: POPOVER_W }}
                className="rounded-xl border border-slate-700/70 bg-slate-800 shadow-2xl shadow-black/50"
                onPointerDown={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-slate-700/50 px-4 py-3">
                  <span className="text-sm font-semibold text-slate-200">
                    Edit window
                  </span>
                  <button
                    type="button"
                    onClick={() => setEditPopover(null)}
                    className="rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-300"
                  >
                    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
                {/* Fields */}
                <div className="space-y-3 px-4 py-3">
                  <label className="block space-y-1">
                    <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                      Start
                    </span>
                    <input
                      type="datetime-local"
                      value={popoverStart}
                      onChange={(e) => applyStart(e.target.value)}
                      className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-200 focus:border-blue-500/60 focus:outline-none"
                    />
                  </label>
                  <label className="block space-y-1">
                    <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                      End
                    </span>
                    <input
                      type="datetime-local"
                      value={popoverEnd}
                      onChange={(e) => applyEnd(e.target.value)}
                      className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-200 focus:border-blue-500/60 focus:outline-none"
                    />
                  </label>
                </div>
              </div>
            );
          })(),
          document.body,
        )}

      <ConfirmDialog
        open={confirmDeleteIdx !== null}
        onClose={() => setConfirmDeleteIdx(null)}
        onConfirm={() => {
          if (confirmDeleteIdx !== null) {
            onChange(windows.filter((_, i) => i !== confirmDeleteIdx));
            setSelectedIdx(null);
            setConfirmDeleteIdx(null);
          }
        }}
        title="Delete active window"
        description="This access window is currently active. Deleting it will immediately remove access for users inside it. Are you sure?"
        confirmLabel="Delete window"
        cancelLabel="Keep it"
        variant="danger"
      />
    </div>
  );
}
